import { useState } from "react";
import type { VocabCardView } from "@storyai/shared";

/**
 * "In this passage, the word X probably means…" with three options.
 * Wrong answers stay red and let the player retry; a correct answer marks
 * the option green, shows the points, and collapses the card.
 */
export function VocabCard({
  card,
  busy,
  onAnswer,
}: {
  card: VocabCardView;
  busy?: boolean;
  /** Resolves with whether the choice was correct and the points awarded. */
  onAnswer: (choice: 0 | 1 | 2) => Promise<{ correct: boolean; points: number } | undefined>;
}) {
  const [wrong, setWrong] = useState<Set<number>>(new Set());
  const [shake, setShake] = useState<number | null>(null);
  const [done, setDone] = useState<{ choice: number; points: number } | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const attempts = card.attempts + wrong.size;

  async function pick(i: 0 | 1 | 2) {
    if (busy || done || wrong.has(i)) return;
    const res = await onAnswer(i);
    if (!res) return;
    if (res.correct) {
      setDone({ choice: i, points: res.points });
      window.setTimeout(() => setCollapsed(true), 1400);
    } else {
      setWrong((s) => new Set(s).add(i));
      setShake(i);
      window.setTimeout(() => setShake(null), 500);
    }
  }

  if (collapsed && done) {
    return (
      <div className="vocab-card vocab-card-done" aria-live="polite">
        <span className="vocab-word">{card.word}</span>
        <span className="muted small"> — {card.options[done.choice]}</span>
        {done.points > 0 && <span className="vocab-points">+{done.points} Reading</span>}
      </div>
    );
  }

  return (
    <div className={`vocab-card${card.kind === "woven" ? " vocab-card-woven" : ""}`} aria-live="polite">
      <div className="vocab-prompt">
        In this passage, the word <strong className="vocab-word">{card.word}</strong> probably means…
      </div>
      <div className="vocab-options" role="group" aria-label={`Meaning of ${card.word}`}>
        {card.options.map((opt, i) => {
          const idx = i as 0 | 1 | 2;
          const cls = ["vocab-option"];
          if (done?.choice === i) cls.push("correct");
          else if (wrong.has(i)) cls.push("wrong");
          if (shake === i) cls.push("shake");
          return (
            <button
              key={i}
              type="button"
              className={cls.join(" ")}
              onClick={() => void pick(idx)}
              disabled={busy || !!done || wrong.has(i)}
            >
              {opt}
            </button>
          );
        })}
      </div>
      <div className="vocab-foot muted small">
        {done ? (
          <span className="vocab-points">{done.points > 0 ? `+${done.points} Reading` : "Correct"}</span>
        ) : (
          <>
            {attempts > 0 ? `${attempts} ${attempts === 1 ? "try" : "tries"} so far · ` : ""}
            <span title="Points for a correct answer on the 1st / 2nd / later try">10 / 5 / 2 points</span>
          </>
        )}
      </div>
    </div>
  );
}
